import { Spinner } from '@forest/shared/solid'
import { Show, type Accessor } from 'solid-js'
import coinIcon from '~/assets/currency/coin.svg'
import gemIcon from '~/assets/currency/gem.svg'
import type { CatalogueCurrency } from '~/lib/catalogue-price'
import { intlLocale } from '~/lib/params'
import { m } from '~/paraglide/messages'
import type { CatalogueProduct } from '~/services/catalogue'
import { canAfford, type ShopBalances } from '~/store/session-ledger'
import {
  createCataloguePriceParts,
  freePriceLabel,
  StruckOriginalPrice,
} from './effective-price-parts'
import { DialogPrimaryButton } from './dialog-buttons'

const numberFormatter = new Intl.NumberFormat(intlLocale)

// A detail's owned tag, in place of its purchase button.
export function DetailOwnedTag(props: { testId: string }) {
  return (
    <div
      data-testid={props.testId}
      class="flex w-full items-center justify-center gap-0.5 rounded-full px-2 py-1 text-sm font-medium leading-[14px] text-[#4BA28B]"
    >
      <svg aria-hidden="true" viewBox="0 0 16 16" class="size-[22px] fill-none">
        <path
          d="m3.25 8.1 3 3 6.5-6.5"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
        />
      </svg>
      {m.owned_tag()}
    </div>
  )
}

// The product detail's action row, shared by the tree and land-skin details:
// the Unlock button at the live effective price — or "Free" — or the owned
// tag. A shortage never opens an insufficient-balance dialog from here — the
// price renders in the unaffordable tint and pressing Unlock escalates to the
// currency-aware top-up navigation (GLOSSARY.md "Insufficient purchase
// balance").
export function DetailUnlockFooter(props: {
  balances: Accessor<ShopBalances | undefined>
  onPurchase: (product: CatalogueProduct) => void
  onTopUp: (currency: CatalogueCurrency) => void
  // A free unlock in flight (GLOSSARY.md "Free product"): with no
  // confirmation to lock, the Unlock button spins and refuses taps.
  pending?: boolean
  product: CatalogueProduct
  serverNow: Accessor<number>
  testIdPrefix: string
}) {
  const paid = createCataloguePriceParts(
    () => props.product.price,
    () => props.serverNow(),
  )
  const priceLabel = () => {
    const parts = paid()
    return parts ? numberFormatter.format(parts.amount) : freePriceLabel()
  }
  // A free product is never unaffordable.
  const shortCurrency = () => {
    const parts = paid()
    if (!parts) return null
    return canAfford(props.balances(), {
      currency: parts.currency,
      price: parts.amount,
    })
      ? null
      : parts.currency
  }

  return (
    <Show
      when={props.product.owned}
      fallback={
        <DialogPrimaryButton
          data-testid={`${props.testIdPrefix}-purchase`}
          disabled={props.pending}
          onClick={() => {
            const short = shortCurrency()
            if (short) props.onTopUp(short)
            else props.onPurchase(props.product)
          }}
        >
          {m.unlock_button_text()}
          <Show
            when={!props.pending}
            fallback={
              <span
                data-testid={`${props.testIdPrefix}-purchase-pending`}
                class="flex items-center px-2 py-1"
              >
                <Spinner class="size-[22px] text-white" />
              </span>
            }
          >
            <span class="flex items-center gap-1 px-2 py-1">
              <Show when={paid()}>
                {(parts) => (
                  <>
                    <img
                      src={parts().currency === 'coin' ? coinIcon : gemIcon}
                      alt=""
                      class="size-[22px]"
                    />
                    <StruckOriginalPrice
                      class="font-normal text-white/60 line-through"
                      original={parts().original}
                      testId={`${props.testIdPrefix}-price-original`}
                    />
                  </>
                )}
              </Show>
              <span
                data-testid={`${props.testIdPrefix}-price`}
                data-affordable={shortCurrency() ? 'false' : 'true'}
                classList={{ 'text-[#F88F8E]': shortCurrency() !== null }}
              >
                {priceLabel()}
              </span>
            </span>
          </Show>
        </DialogPrimaryButton>
      }
    >
      <DetailOwnedTag testId={`${props.testIdPrefix}-owned`} />
    </Show>
  )
}
